import { CommunityProfileAdminsEnum } from '../../enums';
import { AdminToCommunityProfileDto } from '../../models/dto';
import { calcCommunityProfileRoles, CalculatedCommunityRoles, CheckPermissionProps, getCommunityProfile } from './utils';

/**
 * CRUD operations around community admins:
 * invite, read, update, delete
 */

export type CommunityAdminPermissionProps = CheckPermissionProps;

export type ProfileOptions = {
	operationOnProfile: AdminToCommunityProfileDto;
};

export type HasCommunityAdminPermissionOptions = {
	createProfile?: Pick<AdminToCommunityProfileDto, 'roleId'>; // check for volunteer role if provided, if not provided skip check
};
export function hasCommunityAdminAddPermission(
	{
		user,
		selectedCommunity,
		createProfile: operationOnProfile,
	}: CommunityAdminPermissionProps & HasCommunityAdminPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	const { isOwner, isLeader } = communityRoles ?? {};

	const isCreatedProfileVolunteer = operationOnProfile?.roleId === CommunityProfileAdminsEnum.Volunteer;
	const canCreateVolonteerProfile = !operationOnProfile || isCreatedProfileVolunteer;

	// owner can and leader can add only volunteers
	return (isOwner || isLeader) && canCreateVolonteerProfile;
}

export type HasCommunityAdminReadOptions = object; // no additional props, for future use
export function hasCommunityAdminReadPermission(
	{ user, selectedCommunity }: CommunityAdminPermissionProps & HasCommunityAdminReadOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	const { isOwner, isLeader } = communityRoles ?? {};

	// only community owners and leaders can read community admins
	return isOwner || isLeader;
}

export type HasCommunityAdminEditPermissionOptions = ProfileOptions;
export function hasCommunityAdminEditPermission(
	{
		user,
		selectedCommunity,
		operationOnProfile,
	}: CommunityAdminPermissionProps & HasCommunityAdminEditPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity || !operationOnProfile) return false;

	const isOperationOnMyself = user.id === operationOnProfile.adminId;

	if (isOperationOnMyself) return false;

	const isOperationOnOriginalOwner =
		operationOnProfile.roleId === CommunityProfileAdminsEnum.Admin && operationOnProfile.isOriginalOwner;

	const { isOwner } = communityRoles ?? {};

	const ownerPermission = isOwner && !isOperationOnOriginalOwner;

	// owner can edit only other profiles but not original owner
	return ownerPermission;
}

export type HasCommunityAdminDeletePermissionOptions = ProfileOptions;
export function hasCommunityAdminDeletePermission(
	{
		user,
		selectedCommunity,
		operationOnProfile,
	}: CommunityAdminPermissionProps & HasCommunityAdminDeletePermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity || !operationOnProfile) return false;

	const isOperationOnMyself = user.id === operationOnProfile.adminId;

	if (isOperationOnMyself) return false;

	const isOperationOnOriginalOwner = operationOnProfile.isOriginalOwner;

	const { isOwner } = communityRoles ?? {};

	// owners can delete all profiles except original owner
	const ownerPermission = isOwner && !isOperationOnOriginalOwner;

	return ownerPermission;
}

export function getCommunityAdminPermissions(props: CommunityAdminPermissionProps) {
	if (!props.user || !props.selectedCommunity || !props.communityProfiles) return;

	const communityProfile = getCommunityProfile(props.communityProfiles, props.user.id, props.selectedCommunity.id);
	const communityRoles = calcCommunityProfileRoles(communityProfile);

	return {
		canAdd: (options: HasCommunityAdminPermissionOptions = {}) =>
			hasCommunityAdminAddPermission({ ...props, ...options }, communityRoles),
		canRead: (options: HasCommunityAdminReadOptions = {}) =>
			hasCommunityAdminReadPermission({ ...props, ...options }, communityRoles),
		canEdit: (options: HasCommunityAdminEditPermissionOptions) =>
			hasCommunityAdminEditPermission({ ...props, ...options }, communityRoles),
		canDelete: (options: HasCommunityAdminEditPermissionOptions) =>
			hasCommunityAdminDeletePermission({ ...props, ...options }, communityRoles),
	};
}
